// screens/admin/AssociatePage.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/common/Loader";
import { FaTrash, FaEye, FaCheck } from "react-icons/fa";
import WelcomeCard from "../../components/common/WelcomeCard";

const AssociatePage = () => {
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch all associates
  const fetchAssociates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/associate/get-all-associates`);
      const data = Array.isArray(res.data.data) ? res.data.data : [];
      setAssociates(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load associates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociates();
  }, []);

  // Delete associate
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this associate application?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/associate/delete-associate/${id}`);
      setAssociates(prev => prev.filter(a => a._id !== id));
      toast.success("Associate deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete associate");
    }
  };

  // Update associate status
  const handleStatusUpdate = async (id, status) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/associate/edit-associate/${id}`, { status });
      setAssociates(prev => 
        prev.map(a => a._id === id ? { ...a, status } : a)
      );
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // View associate details
  const handleViewDetails = (associate) => {
    setSelectedAssociate(associate);
    setShowDetailModal(true);
  };

  // Custom date formatting
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    hours = hours.toString().padStart(2, '0');
    
    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      reviewed: "bg-green-100 text-green-800",
      default: "bg-gray-100 text-gray-800"
    };
    
    const colorClass = statusColors[status?.toLowerCase()] || statusColors.default;
    
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colorClass}`}>
        {status || "Pending"}
      </span>
    );
  };

  // Employment type badge
  const EmploymentBadge = ({ type }) => {
    const colors = {
      "Full Time": "bg-blue-100 text-blue-800",
      "Part Time": "bg-purple-100 text-purple-800",
    };
    
    return (
      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${colors[type] || "bg-gray-100 text-gray-800"}`}>
        {type || "N/A"}
      </span>
    );
  };

  return (
    <div className="p-4">
      <WelcomeCard />
      
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold">Associate Applications</h1>
        <div className="text-sm text-gray-600">
          Total: <span className="font-semibold">{associates.length}</span>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center mt-10"><Loader /></div>
      ) : associates.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">No associate applications found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border rounded">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-2 px-4 border text-left">Date</th>
                <th className="py-2 px-4 border text-left">Name</th>
                <th className="py-2 px-4 border text-left">Email</th>
                <th className="py-2 px-4 border text-left">Phone</th>
                <th className="py-2 px-4 border text-left">Qualification</th>
                <th className="py-2 px-4 border text-left">Employment</th>
                <th className="py-2 px-4 border text-left">Status</th>
                <th className="py-2 px-4 border text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {associates.map((associate) => (
                <tr key={associate._id} className="hover:bg-gray-50">
                  <td className="py-2 px-4 border whitespace-nowrap">
                    {formatDate(associate.createdAt)}
                  </td>
                  <td className="py-2 px-4 border font-medium">{associate.name}</td>
                  <td className="py-2 px-4 border">{associate.email}</td>
                  <td className="py-2 px-4 border">{associate.phone}</td>
                  <td className="py-2 px-4 border max-w-xs truncate" title={associate.qualification}>
                    {associate.qualification}
                  </td>
                  <td className="py-2 px-4 border">
                    <EmploymentBadge type={associate.employmentType} />
                  </td>
                  <td className="py-2 px-4 border">
                    <StatusBadge status={associate.status} />
                  </td>
                  <td className="py-2 px-4 border text-center">
                    <div className="flex justify-center items-center gap-2">
                      <button 
                        onClick={() => handleViewDetails(associate)} 
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="View details"
                      >
                        <FaEye />
                      </button>
                      {associate.status === "pending" && (
                        <button 
                          onClick={() => handleStatusUpdate(associate._id, "reviewed")} 
                          className="text-green-500 hover:text-green-700 transition-colors"
                          title="Mark as reviewed"
                        >
                          <FaCheck />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDelete(associate._id)} 
                        className="text-red-500 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAssociate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Associate Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Full Name</label>
                  <p className="text-gray-900 font-medium">{selectedAssociate.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-gray-900">{selectedAssociate.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="text-gray-900">{selectedAssociate.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Employment Type</label>
                  <p className="text-gray-900">
                    <EmploymentBadge type={selectedAssociate.employmentType} />
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Qualification</label>
                  <p className="text-gray-900 bg-gray-50 p-2 rounded">{selectedAssociate.qualification}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Residence Address</label>
                  <p className="text-gray-900 bg-gray-50 p-2 rounded">{selectedAssociate.residence}</p>
                </div>
                {selectedAssociate.workExperience && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-gray-500">Work Experience</label>
                    <p className="text-gray-900 bg-gray-50 p-2 rounded">{selectedAssociate.workExperience}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-500">Expected Salary</label>
                  <p className="text-gray-900 font-semibold text-green-600">{selectedAssociate.expectedSalary}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p><StatusBadge status={selectedAssociate.status} /></p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-gray-500">Submitted On</label>
                  <p className="text-gray-900">{formatDate(selectedAssociate.createdAt)}</p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                {selectedAssociate.status === "pending" && (
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedAssociate._id, "reviewed");
                      setShowDetailModal(false);
                    }}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                  >
                    Mark as Reviewed
                  </button>
                )}
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssociatePage;